import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	EmailProviderCode,
	type EmailSettingsRequest,
	type EmailSettingsResponse,
	EmailSettingsWarning,
	type TenantConfigPlane,
} from '@today/types'
import type { EmailSenderDraft } from '@today/ui-domain'
import { isAxiosError } from 'axios'
import { useEffect, useState } from 'react'
import { notificationSettingsService } from '@/services/notification-settings.service'

const EMPTY_DRAFT: EmailSenderDraft = {
	provider: EmailProviderCode.SES,
	fromName: '',
	fromAddress: '',
	replyTo: '',
	unsubscribeMailbox: '',
	accessKeyId: '',
	secretAccessKey: '',
	region: '',
}

const draftOf = (settings: EmailSettingsResponse | undefined): EmailSenderDraft => {
	const binding = settings?.binding
	if (!binding) return EMPTY_DRAFT
	return {
		...EMPTY_DRAFT,
		provider: binding.provider,
		fromName: binding.fromName ?? '',
		fromAddress: binding.fromAddress ?? '',
		replyTo: binding.replyTo ?? '',
		unsubscribeMailbox: binding.unsubscribeMailbox ?? '',
	}
}

/** Display form of the from-address domain, canonical as on the server (lowercase, no trailing root dot). */
const domainOf = (address: string): string =>
	address
		.slice(address.lastIndexOf('@') + 1)
		.toLowerCase()
		.replace(/\.$/, '')

const orNull = (value: string): string | null => (value.trim() === '' ? null : value.trim())

/** The PUT body: credentials only when the admin typed any (absent → the stored ones are kept). */
export function emailRequestOf(draft: EmailSenderDraft): EmailSettingsRequest {
	const typed = [draft.accessKeyId, draft.secretAccessKey, draft.region].some((v) => v.trim() !== '')
	return {
		provider: draft.provider,
		fromName: orNull(draft.fromName),
		fromAddress: orNull(draft.fromAddress),
		replyTo: orNull(draft.replyTo),
		unsubscribeMailbox: orNull(draft.unsubscribeMailbox),
		...(typed
			? {
					credentials: {
						accessKeyId: draft.accessKeyId.trim(),
						secretAccessKey: draft.secretAccessKey,
						region: draft.region.trim(),
					},
				}
			: {}),
	}
}

/** The API's error code and field, as one line for the form. */
export const settingsErrorMessage = (error: unknown): string => {
	const body = isAxiosError(error)
		? (error.response?.data as { error?: { code?: string; details?: { field?: string } } })
		: undefined
	const field = body?.error?.details?.field
	if (field) return `Check the ${field} field.`
	return body?.error?.code ? `Could not save (${body.error.code}).` : 'Could not save — try again.'
}

/**
 * The domain panel's inputs (REQ-010): only for a platform-credential binding —
 * a stale record left from before own credentials were set is not offered for
 * Verify — once there is a record, or while email falls back to the platform
 * address.
 */
export function domainPanelOf(settings: EmailSettingsResponse | undefined): {
	domain: string | null
	fallbackAddress: string | null
} {
	if (!settings?.binding || settings.binding.hasCredentials) return { domain: null, fallbackAddress: null }
	const unverified = settings.warnings.includes(EmailSettingsWarning.SENDER_DOMAIN_UNVERIFIED)
	const fromAddress = settings.binding.fromAddress ?? ''
	return {
		domain: settings.senderDomain?.domain ?? (unverified ? domainOf(fromAddress) : null),
		fallbackAddress: unverified ? settings.effectiveSender.address : null,
	}
}

/**
 * The Email section's sender and domain state (TO-382): load, edit a draft,
 * save / remove, verify the sending domain. Credentials are write-only: the
 * draft starts them empty and clears them after a save.
 */
export function useEmailSettings(tenantId: string, tenantSlug: string, plane: TenantConfigPlane) {
	const client = useQueryClient()
	const key = ['notification-settings', 'email', tenantId, plane]
	const query = useQuery({
		queryKey: key,
		queryFn: () => notificationSettingsService.getEmail(tenantId, tenantSlug, plane),
	})
	const [draft, setDraft] = useState<EmailSenderDraft>(EMPTY_DRAFT)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		if (query.data) setDraft(draftOf(query.data))
	}, [query.data])

	const save = useMutation({
		mutationFn: () => notificationSettingsService.putEmail(tenantId, tenantSlug, plane, emailRequestOf(draft)),
		onSuccess: (settings) => {
			client.setQueryData(key, settings)
			setError(null)
		},
		onError: (e) => setError(settingsErrorMessage(e)),
	})
	const remove = useMutation({
		mutationFn: () => notificationSettingsService.deleteEmail(tenantId, tenantSlug, plane),
		onSuccess: () => client.invalidateQueries({ queryKey: key }),
		onError: (e) => setError(settingsErrorMessage(e)),
	})
	const verify = useMutation({
		mutationFn: () => notificationSettingsService.verifyDomain(tenantId, tenantSlug, plane),
		onSuccess: () => client.invalidateQueries({ queryKey: key }),
		onError: (e) => setError(settingsErrorMessage(e)),
	})

	const settings = query.data
	const panel = domainPanelOf(settings)
	return {
		settings,
		loading: query.isPending,
		loadFailed: query.isError,
		draft,
		error,
		isBusy: save.isPending || remove.isPending || verify.isPending,
		domain: panel.domain,
		fallbackAddress: panel.fallbackAddress,
		onChange: (field: keyof EmailSenderDraft, value: string) => setDraft((d) => ({ ...d, [field]: value })),
		onSave: () => save.mutate(),
		onRemove: () => remove.mutate(),
		onVerify: () => verify.mutate(),
		onCopy: (value: string) => void navigator.clipboard?.writeText(value),
	}
}
